<template>
  <m-dropdown
    class="ai-chat-model-picker"
    :title="$t('aiChat.picker.internalHint')"
  >
    <m-tooltip placement="top">
      <m-btn
        icon="settings"
        hide-border
        fab
        small
      />
      <template #title>
        {{ $t('aiChat.picker.internalHint') }}
      </template>
    </m-tooltip>
    <template #overlay>
      <m-card
        list
        no-padding
        class="_overlay"
      >
        <div class="_hint">
          {{ $t('aiChat.picker.internalHint') }}
        </div>
        <m-card-item
          no-hover
          :clickable="false"
        >
          {{ $t('aiChat.picker.model') }}
          <template #right>
            <m-select
              :value="personalAppSettings.aiChatModel"
              :items="models"
              :placeholder="$t('aiChat.picker.default')"
              clearable
              light
              small
              btn
              hide-border
              @change="pickModel"
            />
          </template>
        </m-card-item>
        <m-card-item
          no-hover
          :clickable="false"
        >
          {{ $t('aiChat.picker.effort') }}
          <template #right>
            <m-select
              :value="effort"
              :items="efforts"
              :disabled="efforts.length === 1"
              light
              small
              btn
              hide-border
              @change="pickEffort"
            />
          </template>
        </m-card-item>
        <m-card-item
          no-hover
          :clickable="false"
        >
          {{ $t('aiChat.picker.webSearch') }}
          <template #right>
            <m-switch
              :value="personalAppSettings.aiChatWebSearch"
              :disabled="!supportsWebSearch(personalAppSettings.aiChatModel)"
              small
              @change="toggleWebSearch"
            />
          </template>
        </m-card-item>
      </m-card>
    </template>
  </m-dropdown>
</template>

<script setup>
import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import usePersonalAppSettings from '@/composables/logged-in-user/personal-app-settings';
import { aiChatModel } from 'shared/constants.json';
import { aiChatModelNames, effortsFor, supportsWebSearch } from '@/lib/ai-chat-models';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const { loggedInUser } = useLoggedInUser();
const { personalAppSettings, updateSingle } = usePersonalAppSettings(loggedInUser);

const models = aiChatModel.all.map((value) => ({ value, text: aiChatModelNames[value] }));
const defaultEffort = 'default';

const allowedEffort = (model, effort) => (effortsFor(model).includes(effort) ? effort : null);

const efforts = computed(() => [
  { value: defaultEffort, text: t('aiChat.picker.default') },
  ...effortsFor(personalAppSettings.value.aiChatModel).map((value) => ({ value, text: t(`aiChat.picker.efforts.${value}`) })),
]);

const effort = computed(() => allowedEffort(personalAppSettings.value.aiChatModel, personalAppSettings.value.aiChatEffort) ?? defaultEffort);

const pickModel = (picked) => {
  const model = picked ?? null;
  updateSingle({
    uid: personalAppSettings.value.uid,
    aiChatModel: model,
    aiChatEffort: allowedEffort(model, personalAppSettings.value.aiChatEffort),
  });
};

const pickEffort = (picked) => updateSingle({ uid: personalAppSettings.value.uid, aiChatEffort: picked === defaultEffort ? null : picked });

const toggleWebSearch = (enabled) => updateSingle({ uid: personalAppSettings.value.uid, aiChatWebSearch: enabled });
</script>

<style
  scoped
  lang="scss"
  type="text/scss"
>
  ._overlay {
    min-width: 26rem;

    ._hint {
      padding: .4rem 1.2rem .6rem;
      font-size: $font-size-2;
      color: map_get($grey, 'lighten-1');
    }
  }
</style>
